import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetPrograms200, GetProgramsQueryParams } from '@eureca/recruiter-api-sdk/types';

/**
 * Loads up to 100 programs to populate program pickers (the "Programa"
 * combobox on the job-create form and the Programa filter on the jobs list).
 * We pin pageSize=100 (the max the BFF accepts) and don't expose pagination —
 * the operator picks one. If a tenant has more than 100 programs, the picker
 * will need a typeahead-driven query (out of scope for the first cut).
 *
 * DEV-315 — `companyId` restricts the list to the programs of ONE company (the
 * "área de trabalho" the consultant entered from the Home). Without it the
 * BFF/Core answer with every program in the consultant's scope — which is how
 * the jobs-list filter ended up offering programs of other companies. Callers
 * that still want the unscoped list (job create/edit pickers) pass nothing.
 */
async function fetchProgramOptions(
  companyId: string | undefined,
  signal?: AbortSignal
): Promise<GetPrograms200> {
  const params: GetProgramsQueryParams = {
    ...(companyId ? { companyId } : {}),
    dir: 'asc',
    page: 1,
    pageSize: 100,
  };
  const res = await fetchClient<GetPrograms200>({
    method: 'GET',
    url: '/programs',
    params,
    signal,
  });
  if (res.error) {
    throw new Error(`Failed to load program options (status ${res.error.status})`);
  }
  return res.data;
}

export function jobProgramOptionsQueryKey(companyId?: string) {
  return ['recruiter', 'programs', 'options', companyId ?? null] as const;
}

export function jobProgramOptionsQueryOptions(companyId?: string) {
  return queryOptions({
    queryKey: jobProgramOptionsQueryKey(companyId),
    queryFn: ({ signal }) => fetchProgramOptions(companyId, signal),
    staleTime: 60_000,
  });
}
